// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import {
  QueryBuilder,
  BaseQueryBuilderPayload,
  Tag,
  StyledModal,
  Button,
  TitleMedium,
  COLORS,
  BASE_COLORS,
  DropdownPopover,
  TitleSmall,
  Switch,
  BodySecondary,
  BodyCaption,
  Tooltip,
} from "@bikdotai/bik-component-library";
import EventsBuilder, { eventsTriggerApiHandler } from "./events";
import { AIInputs } from "./keywords";
import {
  BuilderContainer,
  InstaDropdownContainer,
  InstaDropdownWrapper,
} from "./styled";
import { QueryValidator } from "./queryValidator";
import {
  extractHeaderAndSubHeader,
  prepareQbState,
  reRenderQbNodes,
} from "./utils";
import { populateChannelIdsDropdownForFlow } from "../../services/helpers/domainScoping";
import { useFlowScope } from "../../hooks/useFlowScope";
import { WarnIcon } from "../../icons/warnIcon";
import InfoIcon from "../../icons/infoIcon";
import { Body, Container, Footer, Header } from "../ig-modals/style";
import { useAppSelector } from "../../state/store";
import EmailIcon from "../../icons/email";
import ChevronDownIcon from "../../icons/chevron_down";
import { useAiRouterSettings } from "../../utilities/useAiRouterSettings";
import { useVersionJet } from "../../utilities/useVersionJet";
import { v4 as uuidv4 } from "uuid";
import AutomationCard from "../../ui-components/automation-card";
import PlusIcon from "../../icons/plusIcon";
import AddEvent from "../../icons/addEvent";

interface ChannelOption {
  label: string;
  value: string;
  selected: boolean;
}

interface EmailReceivedBuilderProps {
  visible: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  nodes: any[];
  mode: string;
  storeId: string;
  channel: string;
  currentChannelId?: string;
  aiSettings?: Record<string, { aiEnabled?: boolean; aiDescription?: string }>;
  keywordObject?: Record<string, { AI_EXAMPLES?: string[] }>;
  initialEventNodes?: any[];
  triggerId?: string;
}

const erQueryValidator = new QueryValidator();

const EmailReceivedBuilder: React.FC<EmailReceivedBuilderProps> = (props) => {
  const [channelIdOptions, setChannelIdOptions] = useState<ChannelOption[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<
    Partial<ChannelOption>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [qbKey] = useState(0);

  const qbRef = useRef<any>();
  const qbStateRef = useRef<any>({}); // live QB state from onStateChange
  const qbSavedStateRef = useRef<any>(undefined); // snapshot saved when toggling AI ON
  const variableListRef = useRef<string[]>([]);

  const [isAiEnabled, setIsAiEnabled] = useState<boolean>(false);
  const [aiDescription, setAiDescription] = useState<string>("");
  const [aiExamples, setAiExamples] = useState<string[]>([""]);

  const [eventNodes, setEventNodes] = useState<any[]>(
    props.initialEventNodes ?? []
  );
  const [showEventBuilder, setShowEventBuilder] = useState<boolean>(false);

  const [footerError, setFooterError] = useState<string>(""); // channel + QB footer errors
  const [qbErrorState, setQbErrorState] = useState<any>(undefined); // QB inline errors
  const [aiDescError, setAiDescError] = useState<string>("");
  const [aiExamplesError, setAiExamplesError] = useState<string>("");
  const [eventNodesError, setEventNodesError] = useState<string>("");

  const { isAiRouterExplicitlyEnabled, isAiRouterInTestMode } = useAiRouterSettings();
  const { isVersionApplicabletoPlan } = useVersionJet();
  // Version-jet gate for the email AI router. Defaults to true unless an
  // "EmailAIrouter" entry in releaseJet restricts it to specific store ids.
  const isEmailAiRouterVersionEnabled =
    isVersionApplicabletoPlan("EmailAIrouter");
  const channels: Record<string, any> = useAppSelector(
    (state) => state.flowMetaState.channels!
  );
  const flowMeta: { [key: string]: any } = useAppSelector(
    (state) => state.flowMetaState.flowMeta
  );
  const flowId = useAppSelector((state) => state.homeState.flowId);
  const { domain, domainChannelMap } = useFlowScope();

  const initChannelAiState = (channelKey: string) => {
    setIsAiEnabled(props.aiSettings?.[channelKey]?.aiEnabled ?? false);
    setAiDescription(props.aiSettings?.[channelKey]?.aiDescription ?? "");
    const savedExamples = props.keywordObject?.[channelKey]?.AI_EXAMPLES;
    setAiExamples(savedExamples?.length > 0 ? savedExamples : [""]);
  };

  const clearErrors = () => {
    setFooterError("");
    setQbErrorState(undefined);
    setAiDescError("");
    setAiExamplesError("");
    setEventNodesError("");
  };

  useEffect(() => {
    populateChannelIdsDropdownForFlow({
      channel: props.channel,
      channels,
      setIsLoading,
      setSelectedChannel: (ch: any) => setSelectedChannel(ch),
      setChannelIdOptions,
      currentChannelId: props.currentChannelId,
      domain,
      domainChannelMap,
      flowMetaEntry: flowMeta[flowId],
    });
  }, [domainChannelMap, flowId]);

  useEffect(() => {
    if (channelIdOptions.length === 1) setSelectedChannel(channelIdOptions[0]);
    if (selectedChannel.value === "ERROR" && props.currentChannelId) {
      setFooterError("No account is connected to this trigger. Please check");
    } else {
      setFooterError("");
    }
  }, [channelIdOptions, selectedChannel]);

  // Re-init AI state when user switches channel
  useEffect(() => {
    if (!selectedChannel.value) return;
    clearErrors();
    initChannelAiState(selectedChannel.value);
  }, [selectedChannel]);

  // Re-init everything when modal opens
  useEffect(() => {
    if (!props.visible) return;
    setEventNodes(props.initialEventNodes ?? []);
    clearErrors();
    if (selectedChannel.value) initChannelAiState(selectedChannel.value);
  }, [props.visible]);

  const resetQb = () => {
    qbStateRef.current = {};
    variableListRef.current = [];
    qbSavedStateRef.current = undefined;
    setQbErrorState(undefined);
    qbRef.current?.clearAll("EVENTS_TRIGGER");
  };

  const validateQb = (): { isError: boolean; state: any } => {
    const nodes = qbStateRef.current?.nodes;
    if (!nodes?.length) {
      const emptyErrorState = {
        nodes: [
          {
            type: "EVENTS_TRIGGER",
            metas: [{ key: "triggerName", error: { key: "Required field" } }],
            properties: [],
          },
        ],
      };
      setQbErrorState(emptyErrorState);
      setFooterError("Some inputs are missing or incorrect. Please check.");
      return { isError: true, state: emptyErrorState };
    }
    const [errMsg, validatedState] = erQueryValidator.validate({
      include: qbStateRef.current,
    });
    setQbErrorState({ ...validatedState.include });
    setFooterError(errMsg);
    return { isError: !!errMsg, state: validatedState.include };
  };

  const validateAiInputs = (): boolean => {
    let hasError = false;
    if (!aiDescription) {
      setAiDescError("Please enter a description");
      hasError = true;
    } else {
      setAiDescError("");
    }
    if (!aiExamples.some((e) => e !== "")) {
      setAiExamplesError("Please enter at least one example");
      hasError = true;
    } else {
      setAiExamplesError("");
    }
    if (!eventNodes.length) {
      setEventNodesError("Please add an event");
      hasError = true;
    } else {
      setEventNodesError("");
    }
    return !hasError;
  };

  const handleClose = () => {
    resetQb();
    props.onClose();
  };

  const handleQbSave = () => {
    if (!selectedChannel.value) {
      setFooterError("Please select an account");
      return;
    }
    if (selectedChannel.value === "ERROR") return;

    const { isError, state } = validateQb();
    if (isError) return;

    const { id, subHeader } = extractHeaderAndSubHeader(state);
    const prepared = prepareQbState(state);

    props.onSave({
      ...prepared,
      header: prepared.nodes[0].metas[0].value,
      subHeader,
      id: props.triggerId || id || uuidv4(),
      type: "event",
      variableList: variableListRef.current,
      channelId: selectedChannel.value,
      aiRouted: false,
      aiDescription: "",
      aiExamples: [],
      aiEventNodes: eventNodes,
    });

    resetQb();
    props.onClose();
  };

  const handleAiSave = () => {
    if (!selectedChannel.value) {
      setFooterError("Please select an account");
      return;
    }
    if (selectedChannel.value === "ERROR") return;
    if (!validateAiInputs()) return;

    const { subtitle: subHeader } = getEventSummary();

    props.onSave({
      nodes: eventNodes,
      header: "emailReceived",
      subHeader,
      id: props.triggerId || uuidv4(),
      type: "event",
      variableList: [],
      channelId: selectedChannel.value,
      aiRouted: true,
      aiDescription,
      aiExamples: aiExamples.filter((e) => e !== ""),
    });

    handleClose();
  };

  const getEventSummary = () => {
    if (!eventNodes.length) return { title: "", subtitle: "" };
    const { header, subHeader } = extractHeaderAndSubHeader({
      nodes: eventNodes,
    });
    return { title: header, subtitle: subHeader };
  };

  const eventSummary = getEventSummary();
  const channelDropdown = (
    <InstaDropdownContainer>
      <DropdownPopover
        width="200px"
        disabled={isLoading}
        onSelect={(c: any) => {
          setSelectedChannel(c);
          setChannelIdOptions(
            channelIdOptions.map((d) => ({
              ...d,
              selected: d.value === c.value,
            }))
          );
        }}
        options={channelIdOptions}
      >
        <InstaDropdownWrapper>
          <EmailIcon />
          <TitleSmall className="dropdown-text" style={{ fontWeight: 400 }}>
            {selectedChannel.label || "Select an account"}
          </TitleSmall>
          <ChevronDownIcon color={COLORS.content.primary} />
        </InstaDropdownWrapper>
      </DropdownPopover>
    </InstaDropdownContainer>
  );

  const exceptionsContent = (
    <div>
      <TitleSmall style={{ marginBottom: 8 }}>Add exceptions</TitleSmall>
      {eventNodes.length > 0 ? (
        <AutomationCard
          header={eventSummary.title}
          subHeader={eventSummary.subtitle}
          icon={
            <AddEvent width={16} height={16} color={COLORS.content.secondary} />
          }
          channel="email"
          hasError={false}
          hasWarning={false}
          onClick={() => setShowEventBuilder(true)}
          onCross={() => {
            setEventNodes([]);
            setEventNodesError("");
          }}
        />
      ) : (
        <Button
          matchParentWidth
          style={{ flex: 1, display: "flex", width: "100%" }}
          size="medium"
          buttonText="Add events"
          buttonType="dashBold"
          LeadingIcon={PlusIcon}
          onClick={(e: React.MouseEvent) => {
            setShowEventBuilder(true);
            e.stopPropagation();
          }}
        />
      )}
      {eventNodesError && eventNodes.length === 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginTop: 4,
          }}
        >
          <InfoIcon color={COLORS.content.negative} width={14} height={14} />
          <BodyCaption style={{ color: COLORS.content.negative }}>
            {eventNodesError}
          </BodyCaption>
        </div>
      )}
    </div>
  );

  return (
    <>
      <StyledModal
        zIndex={200}
        centralContainerStyles={{ width: "1032px", height: "560px" }}
        onClose={handleClose}
        open={props.visible}
      >
        <Container>
          {/* Header */}
          <div style={{ display: "flex" }}>
            <Header style={{ borderBottom: "none", paddingBottom: "4px" }}>
              <TitleMedium>Create trigger based on events</TitleMedium>
            </Header>
            <div style={{ marginTop: 15, paddingRight: 15 }}>
              {channelDropdown}
            </div>
          </div>

          {/* AI toggle row — only shown when the store has AI router enabled and the
              EmailAIrouter version is applicable, or if the flow already had an ai trigger */}
          {((isAiRouterExplicitlyEnabled && isEmailAiRouterVersionEnabled) ||
            isAiEnabled) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0px 24px 20px 24px",
                borderBottom: `1px solid ${COLORS.stroke.primary}`,
              }}
            >
              <BodySecondary>Trigger journey using AI instead</BodySecondary>
              <Tooltip
                body="AI automatically triggers the journey when customers asks questions related to the journey."
                width={300}
                placement="bottom"
              >
                <div
                  style={{
                    marginLeft: 4,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <InfoIcon width={16} height={16} />
                </div>
              </Tooltip>
              <div style={{ marginLeft: 12 }}>
                <Tooltip
                  body={
                    !selectedChannel.value
                      ? "Please select an account to continue"
                      : ""
                  }
                >
                  <div>
                    <Switch
                      value={isAiEnabled}
                      type="brand"
                      disabled={!selectedChannel.value}
                      onValueChange={(value: boolean) => {
                        // Persist QB form data so it restores when toggling back
                        if (
                          value &&
                          Object.keys(qbStateRef.current).length > 0
                        ) {
                          qbSavedStateRef.current = qbStateRef.current;
                        }
                        setIsAiEnabled(value);
                      }}
                    />
                  </div>
                </Tooltip>
              </div>
              {isAiRouterInTestMode && (
                <div
                  style={{
                    marginLeft: 12,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Tooltip body="When the toggle is ON: It works only in the BIK lab environment for testing and is not live for end users.">
                    <div style={{ cursor: "pointer", display: "flex" }}>
                      <WarnIcon width="20" height="20" />
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          )}

          {/* Body */}
          {!isAiEnabled ? (
            <Body>
              <BuilderContainer>
                {props.visible && (
                  <QueryBuilder
                    key={qbKey}
                    ref={qbRef}
                    mode={props.mode}
                    state={
                      qbSavedStateRef.current
                        ? qbSavedStateRef.current
                        : props.nodes?.length
                          ? { nodes: reRenderQbNodes(props.nodes) }
                          : undefined
                    }
                    selectorKey="eventTrigger"
                    addNodeBtnText="Add Trigger"
                    queryPreText="Whenever user performs"
                    infinite={false}
                    onStateChange={(state: any) => {
                      qbStateRef.current = state;
                    }}
                    errorState={qbErrorState}
                    typesAPIHandlers={{
                      EVENTS_TRIGGER: (payload: BaseQueryBuilderPayload<any>) =>
                        eventsTriggerApiHandler(
                          payload,
                          props.storeId,
                          props.channel,
                          "EmailReceived",
                          undefined,
                          (list) => {
                            variableListRef.current = list;
                          }
                        ),
                    }}
                  />
                )}
              </BuilderContainer>
            </Body>
          ) : (
            <Body>
              <AIInputs
                aiDescription={aiDescription}
                setAiDescription={setAiDescription}
                aiExamples={aiExamples}
                setAiExamples={setAiExamples}
                aiDescError={aiDescError}
                aiExampleError={aiExamplesError}
                bottomContent={exceptionsContent}
              />
            </Body>
          )}

          {/* Footer */}
          <Footer>
            {footerError ? (
              <Tag
                tagText={footerError}
                type="negative"
                LeadingIcon={WarnIcon}
              />
            ) : (
              <BodySecondary style={{ color: BASE_COLORS.grayscale["700"] }}>
                Create trigger to start any journey as per your use case
              </BodySecondary>
            )}
            <Button
              disabled={isLoading}
              buttonText="Create"
              size="medium"
              onClick={isAiEnabled ? handleAiSave : handleQbSave}
            />
          </Footer>
        </Container>
      </StyledModal>

      {showEventBuilder && (
        <EventsBuilder
          storeId={props.storeId}
          mode={eventNodes.length > 0 ? "edit" : "create"}
          nodes={eventNodes}
          visible={showEventBuilder}
          category="EmailReceived"
          onSave={(data: any) => {
            setEventNodes(data.nodes ?? []);
            setEventNodesError("");
            if (data.channelId && data.channelId !== selectedChannel.value) {
              const matched = channelIdOptions.find(
                (c) => c.value === data.channelId
              );
              if (matched) {
                setSelectedChannel(matched);
                setChannelIdOptions(
                  channelIdOptions.map((c) => ({
                    ...c,
                    selected: c.value === data.channelId,
                  }))
                );
              }
            }
          }}
          onClose={() => setShowEventBuilder(false)}
          onBack={() => setShowEventBuilder(false)}
          channel={props.channel}
          currentChannelId={selectedChannel.value}
          ctaText="Save and continue"
          triggerId={props.triggerId}
          footerCopy="Add conditional events to your trigger"
        />
      )}
    </>
  );
};

export default EmailReceivedBuilder;
